/**
 * Asset vocabularies and flag groups (PRD §5.6).
 *
 * Separate from the components so those files export only components — a module
 * mixing the two defeats fast refresh.
 *
 * The flag tuples mirror the API's own ordering, and the detail page reads them
 * in this sequence so a reviewer sees the same list every time regardless of
 * how the JSON arrived.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.4, PRD §5.6
 */

/** What kind of asset this is. */
export const ASSET_TYPES = [
  'SOLAR_PV',
  'BESS',
  'WIND',
  'HYBRID',
  'THERMAL',
  'BIOMASS',
  'GRID_CONNECTION',
  'CUSTOMER_INDUSTRIAL_SITE',
] as const;

export type AssetType = (typeof ASSET_TYPES)[number];

/**
 * Marker colour per asset type, as CSS values, for the map (F19).
 *
 * A CATEGORICAL palette, not a sentiment one: the colour says *what kind of
 * site*, never "how good" — so it draws from the design-system ramps as
 * distinct hues rather than the green-reserved-for-data rule, which is about
 * values, not categories. Every value is a token (PRD §9.1); none is invented.
 */
export const ASSET_TYPE_COLOR: Record<AssetType, string> = {
  SOLAR_PV: 'var(--stage-1)',
  BESS: 'var(--blue-500)',
  WIND: 'var(--stage-5)',
  HYBRID: 'var(--purple-500)',
  THERMAL: 'var(--stage-3)',
  BIOMASS: 'var(--green-700)',
  GRID_CONNECTION: 'var(--navy-500)',
  CUSTOMER_INDUSTRIAL_SITE: 'var(--stage-7)',
};

/** Who owns it — which decides whose consent a pilot needs. */
export const ASSET_OWNERSHIPS = [
  'GREENVOLT_OWNED',
  'CUSTOMER_OWNED',
  'JV',
  'THIRD_PARTY_MANAGED',
] as const;

export type AssetOwnership = (typeof ASSET_OWNERSHIPS)[number];

/** Where it is in its own lifecycle. */
export const ASSET_STATUSES = [
  'DEVELOPMENT',
  'CONSTRUCTION',
  'OPERATIONAL',
  'DECOMMISSIONING',
] as const;

export type AssetStatus = (typeof ASSET_STATUSES)[number];

/**
 * Dot colour per status.
 *
 * The three pre-operational states walk up the green→navy ramp, so "closer to
 * running" reads the way it does everywhere else. `DECOMMISSIONING` steps
 * outside the ramp: it is a site leaving service, not one progressing towards
 * it, and colouring it as a late stage would read as an achievement.
 */
export const ASSET_STATUS_DOT: Record<AssetStatus, string> = {
  DEVELOPMENT: 'bg-stage-1',
  CONSTRUCTION: 'bg-stage-4',
  OPERATIONAL: 'bg-stage-7',
  DECOMMISSIONING: 'bg-stage-rejected',
};

/** The six flags that argue a site CAN host a pilot. */
export const SUITABILITY_FLAGS = [
  'suitSpareLand',
  'suitSpareGridCapacity',
  'suitKnownCurtailment',
  'suitCommunications',
  'suitStraightforwardAccess',
  'suitQualifiedStaffOnSite',
] as const;

export type SuitabilityFlag = (typeof SUITABILITY_FLAGS)[number];

/** The five flags that argue it cannot, yet. */
export const BLOCKER_FLAGS = [
  'blockPpaRestrictions',
  'blockOmWarrantyLimits',
  'blockLenderConsent',
  'blockSharedOwnership',
  'blockUnderConstruction',
] as const;

export type BlockerFlag = (typeof BLOCKER_FLAGS)[number];
